import { set } from 'lodash';
import React, { createContext, useContext, useState, useEffect } from 'react';
import { isSession } from 'react-router-dom';

// Create the Auth Context
const AuthContext = createContext({
  isAuthenticated: false,
  user: null,
  isSessionExpired: false,
  isProcessingMagicToken: false,
  setAuthenticated: () => { },
  setSessionExpired: () => { },
  setUser: () => { },
  setIsProcessingMagicToken: () => { },
  checkAuthStatus: () => { }
});

// Create the Auth Provider component
export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isSessionExpired, setIsSessionExpired] = useState(false);
  const [isProcessingMagicToken, setIsProcessingMagicToken] = useState(false);
  const [user, setUser] = useState(null);

  // Check authentication status on initial load
  useEffect(() => {
    checkAuthStatus();
  }, []);

  // Function to check if user is authenticated
  const checkAuthStatus = () => {
    const token = localStorage.getItem('access_token');

    if (!token) {
      setIsAuthenticated(false);
      setUser(null);
      return false;
    }

    try {
      // Parse token to check expiration
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const payload = JSON.parse(window.atob(base64));

      // Check if token is expired
      const isValid = payload.exp ? payload.exp > Math.floor(Date.now() / 1000) : true;

      if (isValid) {
        setIsAuthenticated(true);

        // Set basic user info from token (you can enhance this)
        if (!user && payload.sub) {
          setUser({
            id: payload.sub,
            plan: payload.plan || ''
            // Add any other fields from your token as needed
          });
        }

        return true;
      } else {
        // Token expired
        setIsAuthenticated(false);
        setUser(null);
        return false;
      }
    } catch (error) {
      // Invalid token
      setIsAuthenticated(false);
      setUser(null);
      return false;
    }
  };

  // Context value - only expose state and setters
  const value = {
    isAuthenticated,
    user,
    isSessionExpired,
    isProcessingMagicToken,
    setAuthenticated: setIsAuthenticated,
    setSessionExpired: setIsSessionExpired,
    setIsProcessingMagicToken,
    setUser,
    checkAuthStatus
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use the auth context
export const useAuth = () => {
  const context = useContext(AuthContext);

  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
};

export default { AuthProvider, useAuth };